import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { RefreshCw, Sparkles, ExternalLink, LogOut, Clock, CheckCircle2, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { listDrafts, listPublished, startGeneration, getJob, clearToken } from './api';

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('en-GB', {
      day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

const AdminDashboard = ({ onSignOut }) => {
  const [drafts, setDrafts] = useState([]);
  const [published, setPublished] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [d, p] = await Promise.all([listDrafts(), listPublished()]);
      setDrafts(d);
      setPublished(p);
    } catch (err) {
      setError(err.message);
      if (err.message.toLowerCase().includes('session')) onSignOut();
    } finally {
      setLoading(false);
    }
  }, [onSignOut]);

  useEffect(() => { load(); }, [load]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const { job_id: jobId } = await startGeneration();
      toast.info('Drafting article — takes 30–90 seconds…', {
        description: 'Claude Sonnet 4.5 is writing. The dashboard will update automatically.',
      });
      // Poll for job completion.
      const deadline = Date.now() + 3 * 60 * 1000; // 3 min hard cap
      // eslint-disable-next-line no-constant-condition
      while (true) {
        if (Date.now() > deadline) {
          throw new Error('Generation timed out after 3 minutes. Refresh to check for the draft.');
        }
        await new Promise((r) => setTimeout(r, 4000));
        const job = await getJob(jobId);
        if (job.status === 'done') {
          toast.success('Draft ready', { description: 'Scroll to Pending review to open it.' });
          break;
        }
        if (job.status === 'failed') {
          throw new Error(job.error || 'Generation failed');
        }
      }
      await load();
    } catch (err) {
      toast.error('Generation failed', { description: err.message });
    } finally {
      setGenerating(false);
    }
  };

  const handleSignOut = () => {
    clearToken();
    onSignOut();
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <header className="border-b border-slate-800 sticky top-0 bg-slate-950/85 backdrop-blur z-10">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-[0.18em] text-slate-400">Keystone</div>
            <div className="text-base font-medium">Article admin</div>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/" className="text-sm text-slate-400 hover:text-white px-3 py-1.5 rounded-md hover:bg-slate-900 transition-colors" data-testid="admin-back-to-site">Back to site</Link>
            <button onClick={load} className="inline-flex items-center gap-1.5 text-sm text-slate-300 hover:text-white px-3 py-1.5 rounded-md hover:bg-slate-900 transition-colors" data-testid="admin-refresh-btn">
              <RefreshCw className="w-3.5 h-3.5" /> Refresh
            </button>
            <button onClick={handleSignOut} className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-red-300 px-3 py-1.5 rounded-md hover:bg-slate-900 transition-colors" data-testid="admin-signout-btn">
              <LogOut className="w-3.5 h-3.5" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10">
        <div className="mb-8 flex flex-wrap items-center gap-3">
          <button
            onClick={handleGenerate}
            disabled={generating}
            className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 rounded-md px-5 py-2.5 text-sm font-medium transition-colors"
            data-testid="admin-generate-btn"
          >
            <Sparkles className="w-4 h-4" />
            {generating ? 'Generating (30–60 sec)…' : 'Generate new draft'}
          </button>
          <span className="text-xs text-slate-500">One click, one draft. No GitHub, no waiting.</span>
        </div>

        {error && (
          <div className="mb-6 border border-red-900/50 bg-red-950/40 rounded-lg p-4 text-sm text-red-200" data-testid="admin-error">
            {error}
          </div>
        )}

        <section className="mb-14">
          <div className="flex items-baseline justify-between mb-4">
            <h2 className="text-lg font-medium">Pending review</h2>
            <span className="text-xs text-slate-500">
              {loading ? 'loading…' : `${drafts.length} draft${drafts.length === 1 ? '' : 's'}`}
            </span>
          </div>
          {!loading && drafts.length === 0 && (
            <div className="border border-dashed border-slate-800 rounded-lg p-10 text-center text-slate-400" data-testid="admin-no-drafts">
              <FileText className="w-6 h-6 mx-auto mb-3 opacity-60" />
              <p className="text-sm">No drafts. Click <em>Generate new draft</em> above to create one.</p>
            </div>
          )}
          <ul className="space-y-3">
            {drafts.map((a) => (
              <li key={a.id}>
                <Link
                  to={`/admin/review/${a.id}`}
                  className="block border border-slate-800 hover:border-blue-600 rounded-lg p-5 transition-colors group"
                  data-testid={`admin-draft-${a.id}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <div className="text-xs uppercase tracking-[0.18em] text-slate-500 mb-1">
                        {a.topic_category || 'draft'} · <code className="text-slate-400">{a.slug}</code>
                      </div>
                      <div className="text-base font-medium text-white group-hover:text-blue-300 transition-colors truncate">
                        {a.title}
                      </div>
                      <div className="mt-2 text-xs text-slate-500 flex items-center gap-3">
                        <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {formatDate(a.created_at)}</span>
                      </div>
                    </div>
                    <div className="text-xs text-blue-400 flex-shrink-0 whitespace-nowrap">Review →</div>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </section>

        <section>
          <div className="flex items-baseline justify-between mb-4">
            <h2 className="text-lg font-medium">Published</h2>
            <span className="text-xs text-slate-500">{published.length}</span>
          </div>
          <ul className="space-y-2">
            {published.map((a) => (
              <li key={a.id} className="flex items-center justify-between gap-4 py-2 border-b border-slate-900 last:border-0">
                <div className="min-w-0 flex items-center gap-2">
                  <CheckCircle2 className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
                  <span className="text-sm text-slate-300 truncate">{a.title}</span>
                </div>
                <div className="flex items-center gap-3 text-xs text-slate-500 flex-shrink-0">
                  <span>{formatDate(a.published_at || a.created_at)}</span>
                  <a
                    href={`/resources/${a.slug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-slate-400 hover:text-blue-300"
                    data-testid={`admin-published-view-${a.slug}`}
                  >
                    View live <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              </li>
            ))}
          </ul>
        </section>
      </main>
    </div>
  );
};

export default AdminDashboard;
